import http from "./http";

/**
 * Fetch pending notifications (sent but not seen)
 * @param {string} type - Optional filter by notification type
 * @param {string} department - Optional filter by department
 * @returns {Promise<{status: string, count: number, notifications: Array}>}
 */
export async function getPendingNotifications(type = null, department = null) {
  const params = new URLSearchParams();
  if (type) params.append("type", type);
  if (department) params.append("department", department);

  const queryString = params.toString();
  const url = queryString
    ? `notifications/pending?${queryString}`
    : "notifications/pending";

  const response = await http.get(url);
  return response.data;
}

/**
 * Dismiss a single notification
 * @param {string} uuid - Notification UUID
 * @returns {Promise<{status: string, message: string}>}
 */
export async function dismissNotification(uuid) {
  const response = await http.post(`notifications/dismiss/${uuid}`);
  return response.data;
}

/**
 * Dismiss multiple notifications
 * @param {Array<string>} uuids - Array of notification UUIDs
 * @returns {Promise<{status: string, message: string, dismissed_count: number}>}
 */
export async function dismissAllNotifications(uuids) {
  const response = await http.post("notifications/dismiss_all", { uuids });
  return response.data;
}

/**
 * Mark a notification as seen (acknowledged but not dismissed)
 * @param {string} uuid - Notification UUID
 * @returns {Promise<{status: string, message: string}>}
 */
export async function markNotificationAsSeen(uuid) {
  const response = await http.post(`notifications/seen/${uuid}`);
  return response.data;
}

/**
 * Get notification history with pagination
 * @param {Object} options - Query options
 * @param {number} options.limit - Max results (default 50)
 * @param {number} options.offset - Offset for pagination
 * @param {string} options.status - Filter by status
 * @param {string} options.type - Filter by type
 * @param {string} options.department - Filter by department
 * @returns {Promise<{status: string, count: number, total_count: number, pending_count: number, notifications: Array}>}
 */
export async function getNotificationHistory(options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit);
  if (options.offset) params.append("offset", options.offset);
  if (options.status) params.append("status", options.status);
  if (options.type) params.append("type", options.type);
  if (options.department) params.append("department", options.department);

  const queryString = params.toString();
  const url = queryString
    ? `notifications/history?${queryString}`
    : "notifications/history";

  const response = await http.get(url);
  return response.data;
}

/**
 * Get all notifications (both pending and dismissed) for history view
 * @param {Object} options - Query options
 * @returns {Promise<{status: string, count: number, total_count: number, pending_count: number, notifications: Array}>}
 */
export async function getAllNotifications(options = {}) {
  return getNotificationHistory(options);
}

/**
 * Get a single notification by UUID
 * @param {string} uuid - Notification UUID
 * @returns {Promise<{status: string, notification: Object}>}
 */
export async function getNotification(uuid) {
  const response = await http.get(`notifications/${uuid}`);
  return response.data;
}

export default {
  getPendingNotifications,
  dismissNotification,
  dismissAllNotifications,
  markNotificationAsSeen,
  getNotificationHistory,
  getAllNotifications,
  getNotification
};
